<script setup lang="ts">
import { CoreEventSource } from '@apps/main/new-desktop/core/models';
import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';

import { NightsSelector } from '../../../modules/nightsSelector';
import type { HotelSelectionDestinationData } from '../models';

const statsigEventsStore = useStatsigEventsStore();

interface HotelSelectionDestinationProps {
  data: HotelSelectionDestinationData;
}

const props = defineProps<HotelSelectionDestinationProps>();

interface HotelSelectionDestinationEmits {
  (eventName: 'openFilters'): void;
  (eventName: 'changeNumberOfNights', nights: number): void;
  (eventName: 'changeDestination', searchText: string): void;
}

const emit = defineEmits<HotelSelectionDestinationEmits>();
</script>

<template>
  <div class="hotel-selection-destination">
    <div class="destination">
      <button
        class="left-side"
        @click="emit('changeDestination', props.data.destinationText)"
      >
        <p class="destination-text">
          {{ props.data.destinationText }}
        </p>
      </button>
      <div class="right-side">
        <NightsSelector
          :number-of-nights="props.data.numberOfNights"
          avoid-0-nights
          app-select-type="transparent"
          :disabled="false"
          :flicker-margin="{ xSpacing: 'spacing-8' }"
          @change-number-of-nights="emit('changeNumberOfNights', $event)"
          @open-number-of-nights="
            statsigEventsStore.logCoreNightsSelectorOpened({
              source: CoreEventSource.HotelSelection,
            })
          "
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-selection-destination {
  display: flex;
  gap: var(--spacing-8);

  .destination {
    flex-grow: 1;
    height: 64px;
    min-width: 0;

    @include for-less-or-equal-than-1535-px {
      height: 52px;
    }

    display: flex;
    align-items: center;

    background-color: var(--color-white);

    .left-side {
      all: unset;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      overflow: hidden;
      height: 100%;
      width: 100%;

      padding-left: var(--spacing-12);
      @include font-label-l-bold;
      color: var(--color-secondary-40);
      cursor: pointer;
      border: 1px solid var(--color-secondary-93);
      border-top-left-radius: var(--border-radius-8);
      border-bottom-left-radius: var(--border-radius-8);
      transition: background-color var(--transition-short);

      .destination-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        padding-right: var(--spacing-12);
      }

      @include on-hover {
        background-color: var(--color-secondary-93);
      }
    }

    .right-side {
      flex-shrink: 0;
      min-width: 0;
      height: 100%;

      border: 1px solid var(--color-secondary-93);
      border-left: none;
      border-top-right-radius: var(--border-radius-8);
      border-bottom-right-radius: var(--border-radius-8);
      transition: background-color var(--transition-short);

      display: flex;
      align-items: center;

      @include on-hover {
        background-color: var(--color-secondary-93);
      }
    }
  }
}
</style>
